import React, { useState, useEffect } from 'react';
import { Tabs, Tab } from '@mui/material';
import RefillInvoiceForm from '../../components/invoices/RefillInvoiceForm';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Skeleton,
  IconButton,
  Autocomplete,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { invoicesApi, customersApi } from '../../services/api';
import { formatNuf } from '../../utils/formatNuf';
import { isIssuedCreditNote } from '../../utils/invoiceDocument';
import { addDays, format } from 'date-fns';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const VAT_RATES = [
  { value: 0, label: '0% - Esente' },
  { value: 4, label: '4%' },
  { value: 10, label: '10%' },
  { value: 22, label: '22%' },
];

const MeterInvoiceForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEdit = Boolean(id);
  const preselectedCustomerId = searchParams.get('customer');

  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [extraInterventions, setExtraInterventions] = useState([]);
  const [removedExtraInterventionIds, setRemovedExtraInterventionIds] = useState([]);
  const [hasLinkedInterventions, setHasLinkedInterventions] = useState(false);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      // Intestazione
      customer_id: preselectedCustomerId || '',
      nuf: '',
      f_emission: new Date(),
      f_ult_gior: addDays(new Date(), 30),
      f_peri_rif: '',
      // Dati Cliente (in fattura)
      f_nome: '',
      f_cod_cli: '',
      f_cif: '',
      f_piva: '',
      f_localita: '',
      f_citta: '',
      f_prov: '',
      // Dati Impianto
      f_ubicazio: '',
      f_nu_conta: '',
      // Letture
      f_lett_att: 0,
      f_lett_pre: 0,
      f_data_att: null,
      f_data_pre: null,
      // Consumi
      f_cons_lit: 0,
      f_cons_mc: 0,
      f_lire_lit: 0,
      // Importi
      f_gas: 0,
      f_quota_me: 0,
      f_varie: 0,
      f_interventi: 0,
      f_int_caus: '',
      f_var_caus: '',
      f_sped_bol: 0,
      f_tasse: 0,
      f_al_iva: 22,
      f_gas_iva: 0,
      f_tot_parz: 0,
      f_totale: 0,
      // Note
      nota: '',
    },
  });

  // Watch per calcoli automatici
  const watchedValues = watch([
    'f_gas', 'f_quota_me', 'f_varie', 'f_sped_bol', 'f_tasse', 'f_al_iva', 'f_interventi',
    'f_lett_att', 'f_lett_pre', 'f_lire_lit'
  ]);
  
  const fGas = parseFloat(watchedValues[0]) || 0;
  const fQuotaMe = parseFloat(watchedValues[1]) || 0;
  const fVarie = parseFloat(watchedValues[2]) || 0;
  const fSpedBol = parseFloat(watchedValues[3]) || 0;
  const fTasse = parseFloat(watchedValues[4]) || 0;
  const parsedVatRate = parseFloat(watchedValues[5]);
  const fAlIva = Number.isFinite(parsedVatRate) ? parsedVatRate : 22;
  const legacyInterventionAmount = parseFloat(watchedValues[6]) || 0;
  const lettAtt = parseFloat(watchedValues[7]) || 0;
  const lettPre = parseFloat(watchedValues[8]) || 0;
  const prezzoLitro = parseFloat(watchedValues[9]) || 0;
  const linkedInterventionsTotal = Math.round(extraInterventions.reduce(
    (sum, intervention) => sum + (parseFloat(intervention.importo) || 0),
    0
  ) * 100) / 100;
  const interventionsTotal = hasLinkedInterventions ? linkedInterventionsTotal : legacyInterventionAmount;
  
  // Calcoli automatici (lettAtt e lettPre sono già in MC)
  const consumoMc = lettAtt - lettPre;
  const consumoLitri = consumoMc * 4.15;
  const totaleImponibile = Math.round((fGas + fTasse + fQuotaMe + interventionsTotal) * 100) / 100;
  const ivaCalcolata = Math.round(totaleImponibile * (fAlIva / 100) * 100) / 100;
  const totale = Math.round((totaleImponibile + fVarie + ivaCalcolata + fSpedBol) * 100) / 100;

  // Query clienti
  const { data: customersData } = useQuery({
    queryKey: ['customersSearch', customerSearch],
    queryFn: () => customersApi.getAll({ search: customerSearch, limit: 20 }).then(res => res.data),
    enabled: customerSearch.length > 1 || !!preselectedCustomerId
  });

  // Query contratti del cliente selezionato
  const { data: contractsData } = useQuery({
    queryKey: ['customerContracts', selectedCustomer?.id],
    queryFn: () => customersApi.getContracts(selectedCustomer.id).then(res => res.data.data || []),
    enabled: !!selectedCustomer?.id
  });

  // Query fattura esistente
  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.getById(id).then(res => res.data.data),
    enabled: isEdit,
  });

  // Effetto per resettare il form quando la fattura viene caricata
  React.useEffect(() => {
    if (invoice) {
      console.log('[InvoiceForm] Fattura caricata:', invoice);
      
      setSelectedCustomer(invoice.Customer || invoice.customer);
      reset({
        customer_id: invoice.customer_id,
        // Intestazione
        nuf: invoice.nuf || '',
        f_emission: invoice.f_emission ? new Date(invoice.f_emission) : null,
        f_ult_gior: invoice.f_ult_gior ? new Date(invoice.f_ult_gior) : null,
        f_peri_rif: invoice.f_peri_rif || '',
        // Dati Cliente
        f_nome: invoice.f_nome || '',
        f_cod_cli: invoice.f_cod_cli || '',
        f_cif: invoice.f_cif || '',
        f_piva: invoice.f_piva || '',
        f_localita: invoice.f_localita || '',
        f_citta: invoice.f_citta || '',
        f_prov: invoice.f_prov || '',
        // Dati Impianto
        f_ubicazio: invoice.f_ubicazio || '',
        f_nu_conta: invoice.f_nu_conta || '',
        // Letture (convertite in MC per visualizzazione)
        f_lett_att: Math.round((parseFloat(invoice.f_lett_att) || 0) / 4.15 * 100) / 100,
        f_lett_pre: Math.round((parseFloat(invoice.f_lett_pre) || 0) / 4.15 * 100) / 100,
        f_data_att: invoice.f_data_att ? new Date(invoice.f_data_att) : null,
        f_data_pre: invoice.f_data_pre ? new Date(invoice.f_data_pre) : null,
        // Consumi
        f_cons_lit: parseFloat(invoice.f_cons_lit) || 0,
        f_cons_mc: parseFloat(invoice.f_cons_mc) || 0,
        f_lire_lit: parseFloat(invoice.f_lire_lit) || 0,
        // Importi
        f_gas: parseFloat(invoice.f_gas) || 0,
        f_quota_me: parseFloat(invoice.f_quota_me) || 0,
        f_varie: parseFloat(invoice.f_varie) || 0,
        f_interventi: parseFloat(invoice.f_interventi) || 0,
        f_int_caus: invoice.f_int_caus || '',
        f_var_caus: invoice.f_var_caus || '',
        f_sped_bol: parseFloat(invoice.f_sped_bol) || 0,
        f_tasse: parseFloat(invoice.f_tasse) || 0,
        f_al_iva: Number.isFinite(parseFloat(invoice.f_al_iva)) ? parseFloat(invoice.f_al_iva) : 22,
        f_gas_iva: parseFloat(invoice.f_gas_iva) || 0,
        f_tot_parz: parseFloat(invoice.f_tot_parz) || 0,
        f_totale: parseFloat(invoice.f_totale) || 0,
        // Note
        nota: invoice.nota || '',
      });
      setExtraInterventions((invoice.extra_interventions || []).map((intervention) => ({
        ...intervention,
        importo: parseFloat(intervention.importo) || 0,
        data_intervento: intervention.data_intervento
          ? new Date(`${String(intervention.data_intervento).slice(0, 10)}T00:00:00`)
          : null
      })));
      setHasLinkedInterventions((invoice.extra_interventions || []).length > 0);
      setRemovedExtraInterventionIds([]);
    }
  }, [invoice, reset]);

  const updateExtraIntervention = (id, field, value) => {
    setExtraInterventions((current) => current.map((intervention) => (
      intervention.id === id ? { ...intervention, [field]: value } : intervention
    )));
  };

  const removeExtraIntervention = (id) => {
    if (!window.confirm('Rimuovere questo intervento dalla fattura? Tornerà tra gli interventi da fatturare.')) return;
    setExtraInterventions((current) => current.filter((intervention) => intervention.id !== id));
    setRemovedExtraInterventionIds((current) => (
      current.includes(id) ? current : [...current, id]
    ));
  };

  const removeLegacyIntervention = () => {
    if (!window.confirm('Rimuovere l’intervento dalla fattura?')) return;
    setValue('f_interventi', 0, { shouldDirty: true });
    setValue('f_int_caus', '', { shouldDirty: true });
  };

  // Mutation salva
  const saveMutation = useMutation({
    mutationFn: (data) => isEdit 
      ? invoicesApi.update(id, data)
      : invoicesApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(isEdit ? `/crm/fatture/${id}` : `/crm/fatture/${response.data.data.id}`);
    },
  });

  const onSubmit = (data) => {
    // Calcola consumi - le letture nel form sono in MC, le riconverto in litri per il DB
    const lettAttLitri = (data.f_lett_att || 0) * 4.15;
    const lettPreLitri = (data.f_lett_pre || 0) * 4.15;
    const consLit = data.f_cons_lit || (lettAttLitri - lettPreLitri);
    const consMc = data.f_cons_mc || (data.f_lett_att - data.f_lett_pre);
    const linkedInterventionDescription = extraInterventions
      .map((intervention) => {
        const title = String(intervention.dicitura || '').trim();
        const description = String(intervention.descrizione || '').trim();
        return description ? `${title}: ${description}` : title;
      })
      .filter(Boolean)
      .join(' | ');
    const linkedInterventionPayload = hasLinkedInterventions ? {
      extra_interventions: extraInterventions.map((intervention) => ({
        id: intervention.id,
        dicitura: intervention.dicitura,
        descrizione: intervention.descrizione,
        importo: parseFloat(intervention.importo) || 0,
        data_intervento: intervention.data_intervento instanceof Date
          ? format(intervention.data_intervento, 'yyyy-MM-dd')
          : String(intervention.data_intervento || '').slice(0, 10),
        note: intervention.note
      })),
      removed_extra_intervention_ids: removedExtraInterventionIds
    } : {};
    
    const formattedData = {
      ...data,
      ...linkedInterventionPayload,
      f_emission: data.f_emission?.toISOString?.().split('T')[0] || data.f_emission,
      f_ult_gior: data.f_ult_gior?.toISOString?.().split('T')[0] || data.f_ult_gior,
      f_data_att: data.f_data_att?.toISOString?.().split('T')[0] || data.f_data_att,
      f_data_pre: data.f_data_pre?.toISOString?.().split('T')[0] || data.f_data_pre,
      // Arrotondamento a 2 decimali per evitare errori di conversione MC/litri
      f_lett_att: Math.round(lettAttLitri * 100) / 100,
      f_lett_pre: Math.round(lettPreLitri * 100) / 100,
      f_cons_lit: Math.round(consLit * 100) / 100,
      f_cons_mc: Math.round(consMc * 100) / 100,
      f_interventi: interventionsTotal,
      f_int_caus: hasLinkedInterventions ? linkedInterventionDescription : data.f_int_caus,
      f_gas_iva: ivaCalcolata,
      f_tot_parz: totaleImponibile,
      f_totale: totale,
    };
    saveMutation.mutate(formattedData);
  };

  if (isEdit && isLoading) {
    return (
      <Box>
        <Skeleton height={60} sx={{ mb: 2 }} />
        <Card><CardContent><Skeleton height={500} /></CardContent></Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 3 }}>
        <IconButton onClick={() => navigate(isEdit ? `/fatture/${id}` : '/fatture')} size={isMobile ? 'small' : 'medium'}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
          {isEdit ? 'Modifica Fattura' : 'Nuova Fattura'}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        {saveMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Errore: {saveMutation.error?.response?.data?.message || 'Errore sconosciuto'}
          </Alert>
        )}

        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={8}>
            {/* INTESTAZIONE */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>📄 Intestazione Fattura</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="customer_id"
                      control={control}
                      rules={{ required: 'Seleziona un cliente' }}
                      render={({ field }) => (
                        <Autocomplete
                          options={customersData?.customers || []}
                          getOptionLabel={(opt) => opt.business_name || opt.ragione_sociale || `${opt.last_name || opt.cognome || ''} ${opt.first_name || opt.nome || ''}`.trim() || ''}
                          onInputChange={(_, value) => setCustomerSearch(value)}
                          onChange={(_, value) => {
                            field.onChange(value?.id || '');
                            setSelectedCustomer(value);
                            // Auto-compila dati cliente
                            if (value) {
                              setValue('f_nome', value.business_name || value.ragione_sociale || `${value.last_name || value.cognome || ''} ${value.first_name || value.nome || ''}`.trim());
                              setValue('f_cod_cli', value.legacy_code || value.cod_cli || '');
                              setValue('f_cif', value.fiscal_code || value.codice_fiscale || '');
                              setValue('f_piva', value.vat_number || value.partita_iva || '');
                              setValue('f_localita', value.address || value.indirizzo || '');
                              setValue('f_citta', value.city || value.citta || '');
                              setValue('f_prov', value.province || value.provincia || '');
                            }
                          }}
                          value={selectedCustomer}
                          renderInput={(params) => (
                            <TextField {...params} label="Cliente *" error={!!errors.customer_id} />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="nuf"
                      control={control}
                      rules={{ required: 'Obbligatorio' }}
                      render={({ field }) => (
                        <TextField {...field} label="N° Fattura *" fullWidth error={!!errors.nuf} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="f_peri_rif"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Periodo Rif." fullWidth placeholder="es. Gen-Feb 2026" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Controller
                      name="f_emission"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data Emissione"
                          value={field.value}
                          onChange={field.onChange}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Controller
                      name="f_ult_gior"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data Scadenza"
                          value={field.value}
                          onChange={field.onChange}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* DATI CLIENTE IN FATTURA */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>👤 Dati Cliente (in fattura)</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="f_nome"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Intestatario" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_cod_cli"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Cod. Cliente" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_cif"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Codice Fiscale" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_piva"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="P. IVA" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Controller
                      name="f_localita"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Indirizzo" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_citta"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Città" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <Controller
                      name="f_prov"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Prov." fullWidth inputProps={{ maxLength: 2 }} />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* DATI IMPIANTO */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>🏠 Dati Impianto</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Controller
                      name="f_ubicazio"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Ubicazione Impianto" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="f_nu_conta"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Matricola Contatore" fullWidth />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* LETTURE E CONSUMI */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>📊 Letture e Consumi</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_lett_pre"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Lettura Precedente" type="number" fullWidth 
                          inputProps={{ step: 0.01 }}
                          InputProps={{ endAdornment: <InputAdornment position="end">MC</InputAdornment> }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_data_pre"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data Lett. Prec."
                          value={field.value}
                          onChange={field.onChange}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_lett_att"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Lettura Attuale" type="number" fullWidth
                          inputProps={{ step: 0.01 }}
                          InputProps={{ endAdornment: <InputAdornment position="end">MC</InputAdornment> }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_data_att"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data Lett. Att."
                          value={field.value}
                          onChange={field.onChange}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField 
                      label="Consumo Lt" 
                      value={consumoLitri.toFixed(2)} 
                      fullWidth 
                      disabled
                      InputProps={{ endAdornment: <InputAdornment position="end">Lt</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField 
                      label="Consumo MC" 
                      value={consumoMc.toFixed(2)} 
                      fullWidth 
                      disabled
                      InputProps={{ endAdornment: <InputAdornment position="end">MC</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Controller
                      name="f_lire_lit"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Prezzo/Lt" type="number" fullWidth
                          inputProps={{ step: 0.0001 }}
                          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* IMPORTI */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>💰 Importi</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_gas"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Importo GPL" type="number" fullWidth
                          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_tasse"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Accise" type="number" fullWidth
                          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_quota_me"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Quota Fissa" type="number" fullWidth
                          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_sped_bol"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Spedizione" type="number" fullWidth
                          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_varie"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Varie/Sconto" type="number" fullWidth
                          helperText="Negativo = sconto"
                          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_var_caus"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Causale Varie" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="f_al_iva"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Aliquota IVA</InputLabel>
                          <Select {...field} label="Aliquota IVA">
                            {VAT_RATES.map(r => (
                              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {isEdit && (hasLinkedInterventions || parseFloat(invoice?.f_interventi) || invoice?.f_int_caus) && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Interventi Extra</Typography>
                  <Divider sx={{ mb: 2 }} />

                  {hasLinkedInterventions ? (
                    <Box>
                      {extraInterventions.map((intervention, index) => (
                        <Box key={intervention.id}>
                          <Grid container spacing={2} alignItems="center" sx={{ py: 1.5 }}>
                            <Grid item xs={12} sm={6} md={3}>
                              <DatePicker
                                label="Data intervento"
                                value={intervention.data_intervento}
                                onChange={(value) => updateExtraIntervention(intervention.id, 'data_intervento', value)}
                                slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Dicitura"
                                value={intervention.dicitura || ''}
                                onChange={(event) => updateExtraIntervention(intervention.id, 'dicitura', event.target.value)}
                                fullWidth
                                required
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Descrizione"
                                value={intervention.descrizione || ''}
                                onChange={(event) => updateExtraIntervention(intervention.id, 'descrizione', event.target.value)}
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={10} sm={5} md={2}>
                              <TextField
                                label="Importo"
                                type="number"
                                value={intervention.importo}
                                onChange={(event) => updateExtraIntervention(intervention.id, 'importo', event.target.value)}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                fullWidth
                                required
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={2} sm={1} md={1} sx={{ textAlign: 'center' }}>
                              <Tooltip title="Rimuovi dalla fattura">
                                <IconButton
                                  color="error"
                                  onClick={() => removeExtraIntervention(intervention.id)}
                                  aria-label={`Rimuovi intervento ${index + 1}`}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Note"
                                value={intervention.note || ''}
                                onChange={(event) => updateExtraIntervention(intervention.id, 'note', event.target.value)}
                                fullWidth
                                size="small"
                              />
                            </Grid>
                          </Grid>
                          {index < extraInterventions.length - 1 && <Divider />}
                        </Box>
                      ))}
                      {extraInterventions.length === 0 && (
                        <Typography color="text.secondary">Nessun intervento resterà collegato alla fattura.</Typography>
                      )}
                      {removedExtraInterventionIds.length > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          {removedExtraInterventionIds.length} intervento/i verranno riportati tra quelli da fatturare.
                        </Alert>
                      )}
                    </Box>
                  ) : (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="f_interventi"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} label="Importo intervento" type="number" fullWidth
                              inputProps={{ min: 0, step: 0.01 }}
                              InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={10} sm={7}>
                        <Controller
                          name="f_int_caus"
                          control={control}
                          render={({ field }) => <TextField {...field} label="Causale intervento" fullWidth />}
                        />
                      </Grid>
                      <Grid item xs={2} sm={1} sx={{ textAlign: 'center' }}>
                        <Tooltip title="Rimuovi dalla fattura">
                          <IconButton color="error" onClick={removeLegacyIntervention} aria-label="Rimuovi intervento">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            )}

            {/* NOTE */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>📝 Note</Typography>
                <Divider sx={{ mb: 2 }} />
                <Controller
                  name="nota"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Note" fullWidth multiline rows={3} />
                  )}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* RIEPILOGO LATERALE */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: { xs: 'static', md: 'sticky' }, top: { md: 80 } }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>📋 Riepilogo</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Importo GPL</Typography>
                    <Typography>{formatCurrency(fGas)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Accise</Typography>
                    <Typography>{formatCurrency(fTasse)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Quota Fissa</Typography>
                    <Typography>{formatCurrency(fQuotaMe)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Interventi Extra</Typography>
                    <Typography>{formatCurrency(interventionsTotal)}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography fontWeight="medium">Imponibile</Typography>
                    <Typography fontWeight="medium">{formatCurrency(totaleImponibile)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Varie/Sconto</Typography>
                    <Typography color={fVarie < 0 ? 'error.main' : 'inherit'}>{formatCurrency(fVarie)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">IVA ({fAlIva}%)</Typography>
                    <Typography>{formatCurrency(ivaCalcolata)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Spedizione</Typography>
                    <Typography>{formatCurrency(fSpedBol)}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">TOTALE</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {formatCurrency(totale)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<SaveIcon />}
                    disabled={saveMutation.isPending}
                  >
                    {saveMutation.isPending ? 'Salvataggio...' : 'Salva Fattura'}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate(isEdit ? `/fatture/${id}` : '/fatture')}
                  >
                    Annulla
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};


const InvoiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState(params.get('billing_type') === 'refill' ? 'refill' : 'meter');
  const query = useQuery({
    queryKey: ['invoiceEditor', id],
    queryFn: () => invoicesApi.getById(id).then(r => r.data.data || r.data),
    enabled: !!id
  });
  if (id && query.isLoading) return <Skeleton height={300}/>;
  if (query.error) return <Alert severity="error">Impossibile caricare la fattura.</Alert>;
  if (isIssuedCreditNote(query.data)) return <Alert severity="info">Questa nota di credito è già emessa. Per correggerla, annullala e crea una nuova nota. <Button onClick={() => navigate('/crm/fatture/' + id)}>Apri nota di credito</Button></Alert>;
  const actualMode = id ? query.data?.billing_type || 'meter' : mode;
  return <Box>
    {!id && <Tabs value={mode} onChange={(_, v) => { setMode(v); const next = new URLSearchParams(params); next.set('billing_type', v); setParams(next, { replace: true }); }} sx={{ mb: 3 }}>
      <Tab value="meter" label="Da contatore"/><Tab value="refill" label="Da rifornimenti"/>
    </Tabs>}
    {actualMode === 'refill' ? <RefillInvoiceForm key={id || 'new'} invoice={query.data || null}/> : <MeterInvoiceForm/>}
  </Box>;
};
export default InvoiceForm;
